import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { toStructuredQuery, toMatchableData, matchItem } from 'teacher-ui-client/utils/search'
import { Product } from 'teacher-ui-client/utils/products'
import { assert } from '@ember/debug'
import { LessonsPage } from 'teacher-ui-client/components/page/lessons'
import { FastPhonicsNav } from 'teacher-ui-client/components/lessons/fast-phonics/nav'
import { FastPhonicsPhaseFilters } from 'teacher-ui-client/components/lessons/fast-phonics/phase-filters'
import { LessonsSearch } from 'teacher-ui-client/components/lessons/search'
import { LessonResource } from 'teacher-ui-client/components/tiles/lesson/resource'
import { FastPhonicsLessonData } from 'teacher-ui-client/components/lessons/fast-phonics/lesson-data'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import { t } from 'ember-intl'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type FpLessonsResourcesPage from 'teacher-ui-client/subscription-type/lessons/fast-phonics/resources/route'

interface Args {
  model: ModelFor<FpLessonsResourcesPage>
}

interface LessonResourceViewModel {
  id: string
  title: string
  coverUrl?: string
  fileUrl?: string
  videoUrl?: string
  lessonData: LessonDataViewModel
}

interface LessonDataViewModel {
  activityNumber: number
  focusSounds?: string[]
  wordsRead?: string[]
  alternatives?: string[]
  wordCount?: number
  phase?: number
}

export class LessonsFastPhonicsResourcesPage extends Component<Args> {
  @service router!: RouterService

  product = Product.FP

  get lessonResourceViewModels(): LessonResourceViewModel[] {
    if (!this.args.model.loaderInstance?.isSuccessful) return []

    let entries = this.args.model.loaderInstance.value ?? []
    const query = this.router.currentRoute?.queryParams.searchQuery

    if (typeof query === 'string' && query.length > 0) {
      const structuredQuery = toStructuredQuery(query)

      entries = entries.filter((entry) => {
        const { title = '' } = entry.fields
        const { focusSounds = [], wordsRead = [] } = entry.fields.lessonData?.fields ?? {}
        const matchableData = toMatchableData([title, focusSounds.join(' '), wordsRead.join(' ')])

        return matchItem(matchableData, structuredQuery)
      })
    }

    return (
      entries
        .map((entry) => {
          const lessonResource = entry.fields
          assert('lessonData must be resolved', entry.fields.lessonData)
          const lessonData = entry.fields.lessonData.fields
          return {
            id: entry.sys.id,
            title: lessonResource.title,
            coverUrl: lessonResource.cover?.fields.file?.url,
            fileUrl: lessonResource.file?.fields.file?.url,
            videoUrl: lessonResource.videoUrl,
            lessonData: {
              activityNumber: lessonData.activityNumber,
              focusSounds: lessonData.focusSounds,
              wordsRead: lessonData.wordsRead,
              alternatives: lessonData.alternatives,
              phase: lessonData.phase,
            },
          }
        })
        .sort((a, b) => a.lessonData.activityNumber - b.lessonData.activityNumber) ?? []
    )
  }

  <template>
    <LessonsPage
      @title={{t "pages.product.lessons.title" product=(t "products.fast-phonics")}}
      @loaderInstance={{@model.loaderInstance}}
    >
      <:header>
        <FastPhonicsNav />
        <LessonsSearch @themeKey={{ThemeKeys.blue}} />
        <FastPhonicsPhaseFilters />
      </:header>
      <:results>
        {{#each this.lessonResourceViewModels as |viewModel|}}
          <LessonResource
            data-test-lesson-resource-entry={{viewModel.lessonData.activityNumber}}
            class="w-80"
            @entryId={{viewModel.id}}
            @title={{viewModel.title}}
            @coverUrl={{viewModel.coverUrl}}
            @fileUrl={{viewModel.fileUrl}}
            @videoUrl={{viewModel.videoUrl}}
          >
            <FastPhonicsLessonData @lessonData={{viewModel.lessonData}} />
          </LessonResource>
        {{else}}
          <NoResultsMessage data-test-empty />
        {{/each}}
      </:results>
    </LessonsPage>
  </template>
}

export default RouteTemplate(LessonsFastPhonicsResourcesPage)
